import { basicAuth, oauthAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import logo from "./logo.svg";

const keywords =
  "snow itsm cmdb incidents tickets change requests table api now platform";
const defaultUrlPattern = () => "https://[^/]*\\.service-now\\.com/.*";
const instanceItem = {
  kind: "hostname",
  label: "Instance hostname",
  required: true,
  placeholder: "yourinstance.service-now.com",
  target: { kind: "configuration", field: "instance" },
  helperText: "Your ServiceNow hostname, such as yourinstance.service-now.com.",
} as const;
const connectionTestUrl = ({ instance }: Record<string, string>) =>
  `https://${instance}/api/now/table/sys_user?sysparm_limit=1`;
const basicConnectionTest = {
  protocol: "http" as const,
  request: connectionTestUrl,
};
const oauthConnectionTest = {
  protocol: "http" as const,
  request: (configuration: Record<string, string>) => ({
    url: connectionTestUrl(configuration),
    expectedStatus: [200, 403],
  }),
};
const publicEnvVars = ({ instance }: Record<string, string>) => ({
  SERVICE_NOW_URL: instance ? `https://${instance}` : "",
});

export const serviceNow = defineProduct({
  name: "ServiceNow",
  tagLine: "IT service management platform",
  logo,
  tags: ["featured", "it", "security"],
  variants: [
    productVariant({
      identifier: "serviceNowOAuthClientCredentials",
      authLabel: "OAuth 2.0 client credentials",
      keywords,
      protocol: "http",
      defaultUrlPattern,
      auth: oauthAuth,
      steps: [
        {
          title: "Get your ServiceNow OAuth credentials",
          items: [
            {
              kind: "markdown",
              content:
                "Follow ServiceNow’s [client credentials configuration](https://www.servicenow.com/docs/r/platform-security/authentication/configure-an-oauth-client-credential-grant.html) to create an active inbound integration, grant only the REST API auth scopes your workflows need, and copy its client ID and client secret. Depending on your configuration, an administrator may also need to [enable client credentials and assign an OAuth Application User](https://www.servicenow.com/docs/r/platform-security/authentication/client-credentials.html).",
            },
          ],
        },
        {
          title: "Enter your credentials",
          items: [
            instanceItem,
            {
              kind: "input",
              label: "Client ID",
              required: true,
              target: { kind: "oauthConnection", field: "clientId" },
            },
            {
              kind: "input",
              label: "Client secret",
              required: true,
              target: { kind: "oauthConnection", field: "clientSecret" },
            },
            {
              kind: "input",
              label: "Scopes",
              helperText:
                "Space-separated. Leave blank if the OAuth application doesn’t use scopes.",
              target: { kind: "oauthConnection", field: "scopes" },
            },
          ],
        },
      ],
      oauth: {
        grantType: () => "client_credentials",
        clientCredentialsFrom: "db",
        tokenEndpoint: ({ instance }) => `https://${instance}/oauth_token.do`,
        tokenEndpointAuthMethod: () => "client_secret_post",
        scopes: () => [],
      },
      connectionTest: oauthConnectionTest,
      publicEnvVars,
    }),
    productVariant({
      identifier: "serviceNow",
      authLabel: "Username and password",
      keywords,
      protocol: "http",
      defaultUrlPattern,
      auth: (app) =>
        basicAuth({
          username: app.config.username,
          password: app.secret.password,
        }),
      steps: [
        {
          title: "Get your ServiceNow credentials",
          items: [
            {
              kind: "markdown",
              content:
                "You’ll need your ServiceNow instance hostname and a username and password with API access.",
            },
          ],
        },
        {
          title: "Enter your credentials",
          items: [
            instanceItem,
            {
              kind: "input",
              label: "Username",
              required: true,
              target: { kind: "configuration", field: "username" },
            },
            {
              kind: "input",
              label: "Password",
              required: true,
              target: { kind: "secrets", field: "password" },
            },
          ],
        },
      ],
      connectionTest: basicConnectionTest,
      publicEnvVars,
    }),
  ],
});
